import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useOktaCallback } from '../../hooks/use-okta-only-auth'
import { isAuthenticated } from '../../api/unified-auth'
import { oktaAuth } from '../../config/okta'

export const Route = createFileRoute('/login/callback')({
  component: CallbackPage,
  beforeLoad: async () => {
    // If user is already authenticated and this isn't a callback flow, redirect to dashboard
    if ((await isAuthenticated()) && !oktaAuth.isLoginRedirect()) {
      throw redirect({ to: '/' })
    }

    // If this isn't a callback flow at all, redirect to login
    const urlParams = new URLSearchParams(window.location.search)
    const hasCallbackParams =
      urlParams.has('code') || urlParams.has('state') || oktaAuth.isLoginRedirect()

    if (!hasCallbackParams) {
      throw redirect({ to: '/login' })
    }
  },
})

function CallbackPage() {
  const oktaCallback = useOktaCallback()
  const navigate = useNavigate()
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Prevent multiple executions in StrictMode or re-renders
    if (hasInitialized.current || oktaCallback.isPending) {
      return
    }

    hasInitialized.current = true

    // Handle the callback immediately when component mounts
    const handleCallback = async () => {
      try {
        // Double-check that we're in a valid callback flow before proceeding
        const urlParams = new URLSearchParams(window.location.search)
        const hasCallbackParams =
          urlParams.has('code') || urlParams.has('state') || oktaAuth.isLoginRedirect()

        if (!hasCallbackParams) {
          // Clean up any problematic session storage
          const sessionRedirect = sessionStorage.getItem('oktaRedirectUri')
          if (
            sessionRedirect &&
            (sessionRedirect.includes('error=') ||
              sessionRedirect.includes('callback_failed') ||
              sessionRedirect.startsWith('/login'))
          ) {
            sessionStorage.removeItem('oktaRedirectUri')
          }

          // If user is already authenticated, go to dashboard, otherwise go to login
          if (await isAuthenticated()) {
            navigate({ to: '/', replace: true })
          } else {
            navigate({ to: '/login', replace: true })
          }
          return
        }

        // Use mutateAsync to handle the callback and get the result
        await oktaCallback.mutateAsync()

        // Give React Query a moment to update the cache
        await new Promise(resolve => setTimeout(resolve, 100))

        // Get the originally intended destination from URL params or fallback to dashboard
        const sessionRedirect = sessionStorage.getItem('oktaRedirectUri')

        // Ensure we don't redirect back to callback or login pages (including error URLs)
        let redirectTo = sessionRedirect || '/'
        if (
          redirectTo === '/login/callback' ||
          redirectTo === '/login' ||
          redirectTo.startsWith('/login') ||
          redirectTo.includes('error=') ||
          redirectTo.includes('callback_failed')
        ) {
          redirectTo = '/'
        }

        // Clear stored redirect URI
        sessionStorage.removeItem('oktaRedirectUri')

        // Redirect to intended destination (clear query params to prevent infinite loop)
        navigate({ to: redirectTo as any, search: undefined, replace: true })
      } catch (error) {
        console.error('Callback handling failed:', error)

        // Check if this is a "not in callback flow" error
        if (error instanceof Error && error.message.includes('Not in an Okta callback flow')) {
          // If already authenticated, go to dashboard
          if (await isAuthenticated()) {
            navigate({ to: '/', replace: true })
            return
          }
        }

        // Reset the flag on error so user can retry
        hasInitialized.current = false
        // Redirect to login with error
        navigate({ to: '/login', search: { error: 'callback_failed' }, replace: true })
      }
    }

    handleCallback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty - we only want this to run once on mount

  if (oktaCallback.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Authentication Failed</h1>
          <p className="text-gray-600 mb-4">There was an error processing your authentication.</p>
          <a href="/login" className="text-blue-600 hover:text-blue-800 underline">
            Return to Login
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h1 className="text-2xl font-bold mb-2">Completing Sign In</h1>
        <p className="text-gray-600">Please wait while we complete your authentication...</p>
      </div>
    </div>
  )
}
